import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, FileText, ShieldCheck, Users } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AddApplicantDialog } from "@/components/AddApplicantDialog";
import { CaseQueue } from "@/components/CaseQueue";
import { useCases } from "@/hooks/useCases";
import { useUpdatedAgo } from "@/hooks/useUpdatedAgo";
import { getCaseStatus, isClosedCase } from "@/lib/caseStatus";
import { initClarity } from "@/lib/clarity";
import { useOperatorVertical } from "@/hooks/useOperatorMe";
import { copyForVertical } from "@/lib/verticalCopy";
import { toTitleCase } from "@/lib/utils";

/**
 * /recruiting/dashboard — the hiring-side operator queue.
 *
 * Same <CaseQueue /> as the Verification Center, under different framing: a
 * hero that says what the surface is for, three counts off the top of the
 * queue, and a short description of the applicant's path. Everything below
 * the hero is the shared component.
 *
 * Renders under the refreshed theme by route (see src/lib/theme.ts), so the
 * violet accents and rounder cards come from the tokens rather than from
 * anything here.
 *
 * ## What this page deliberately does NOT do
 *
 * It does not restate queue vocabulary. Status pills and "what's happening"
 * copy stay in lib/caseStatus.ts, which CLAUDE.md names as the single source
 * of truth for the operator queue — a recruiting-flavoured variant belongs
 * there, keyed off the vertical, and is tracked in issue #154.
 *
 * It also carries no empty state of its own: <CaseQueue /> already renders
 * one, and a second would double up on a queue with no cases.
 */

const JOURNEY_STEPS = [
  {
    title: "Application",
    body: (noun: string) =>
      `The ${noun} works through a guided intake instead of a form dump.`,
    icon: FileText,
    tone: "bg-card text-card-foreground border-border",
  },
  {
    title: "Identity context",
    body: () => "Identity, account history, and vouches are gathered into one view.",
    icon: ShieldCheck,
    tone: "bg-gradient-to-br from-violet-600 to-indigo-600 text-white border-violet-500",
  },
  {
    title: "Your review",
    body: () => "You open a case with the context already gathered and the gaps named.",
    icon: Users,
    tone: "bg-card text-card-foreground border-border",
  },
];

export default function RecruitingDashboard() {
  // Vocabulary comes from the copy deck, not from literals on this page —
  // decision 8's visible word is still open, and a hardcoded "candidate" here
  // beside an "applicant" in the shared queue is the split that made it a
  // review finding.
  const { vertical } = useOperatorVertical();
  const copy = copyForVertical(vertical);

  useEffect(() => {
    initClarity();
  }, []);

  const [addOpen, setAddOpen] = useState(false);
  const { cases, loading, refetch } = useCases({ includeClosed: true });
  const updatedAgo = useUpdatedAgo(loading);

  // Three headline counts. Derived here rather than lifted out of CaseQueue:
  // that component's full per-kind map drives its tab strip and has to stay
  // in step with it, and this is three of the eight kinds. Closed cases are
  // excluded on the same rule the queue table uses.
  const stats = useMemo(() => {
    let open = 0;
    let readyForReview = 0;
    let awaitingVouches = 0;
    for (const c of cases) {
      if (isClosedCase(c)) continue;
      open += 1;
      const kind = getCaseStatus(c).kind;
      if (kind === "submitted") readyForReview += 1;
      if (kind === "awaiting_vouches") awaitingVouches += 1;
    }
    return { open, readyForReview, awaitingVouches };
  }, [cases]);

  const scrollToQueue = () => {
    document.getElementById("applicant-list")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <div className="space-y-6">
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]"
      >
        <div className="relative overflow-hidden rounded-[2rem] border border-border bg-card/90 p-6 sm:p-8 shadow-sm backdrop-blur-sm">
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute -left-16 -top-20 h-56 w-56 rounded-full bg-violet-500/10 blur-3xl" />
            <div className="absolute right-0 top-6 h-44 w-44 rounded-full bg-sky-400/10 blur-3xl" />
          </div>

          <div className="relative flex flex-wrap items-center justify-between gap-3">
            <Badge
              variant="outline"
              className="rounded-full border-violet-200 bg-violet-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-violet-700"
            >
              Hiring
            </Badge>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground shadow-sm">
              <ArrowUpRight className="h-3.5 w-3.5 text-violet-600" />
              Updated {updatedAgo}
            </span>
          </div>

          <div className="relative mt-6 max-w-3xl">
            <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground sm:text-5xl leading-[1.05]">
              {toTitleCase(copy.applicantNounPlural)} build a stronger application.
              <span className="block text-violet-700">You get a clearer first read.</span>
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg">
              {toTitleCase(copy.applicantNounPlural)} add identity context, account history,
              and people who will vouch for them. You see what was gathered and
              what is still missing, in one place.
            </p>
          </div>

          <div className="relative mt-6 flex flex-wrap gap-3">
            <Button
              onClick={() => setAddOpen(true)}
              className="h-11 rounded-full px-5 font-semibold shadow-sm"
            >
              Send request
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              onClick={scrollToQueue}
              className="h-11 rounded-full px-5 font-semibold"
            >
              View queue
            </Button>
          </div>

          <div className="relative mt-8 grid gap-3 sm:grid-cols-3">
            {[
              { label: "Open cases", value: stats.open, detail: "Currently in the queue." },
              { label: "Ready for review", value: stats.readyForReview, detail: "Waiting on you." },
              { label: "Awaiting vouches", value: stats.awaitingVouches, detail: "Still gathering support." },
            ].map((item) => (
              <div key={item.label} className="rounded-2xl border border-border bg-card/90 p-4 shadow-sm">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  {item.label}
                </div>
                <div className="mt-2 text-3xl font-semibold tracking-tight text-foreground">
                  {item.value}
                </div>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">{item.detail}</p>
              </div>
            ))}
          </div>
        </div>

        <Card className="overflow-hidden rounded-[2rem] border-border bg-card/90 shadow-sm backdrop-blur-sm">
          <CardContent className="p-6 sm:p-7">
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              How it works
            </div>
            <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight text-foreground">
              From application to your review.
            </h2>

            <div className="mt-6 space-y-3">
              {JOURNEY_STEPS.map((step) => {
                const Icon = step.icon;
                const onAccent = step.tone.includes("gradient");
                return (
                  <div key={step.title} className={`rounded-2xl border p-4 ${step.tone}`}>
                    <div className="flex items-start gap-3">
                      <div
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                          onAccent ? "bg-white/15" : "bg-violet-50 text-violet-600"
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-base font-semibold tracking-tight">{step.title}</div>
                        <p
                          className={`mt-1 text-sm leading-6 ${
                            onAccent ? "text-white/80" : "text-muted-foreground"
                          }`}
                        >
                          {step.body(copy.applicantNoun)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </motion.section>

      <AddApplicantDialog open={addOpen} onOpenChange={setAddOpen} onCreated={refetch} />

      <CaseQueue onSendRequest={() => setAddOpen(true)} />
    </div>
  );
}
